import React, { useState, useEffect } from "react";
import { Container, Form, Header, Icon, Breadcrumb, Button, Grid, Placeholder, Segment, Message, Dropdown } from 'semantic-ui-react';
import { Link, useParams } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import DayPicker, { DayModifiers, Modifier } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import moment from 'moment';
import { TimeSlot } from './TimeSlot';
import { getSpecialistMeetingListAssignedToMaster, getSpecialistMeetingRequestById, saveRSAStaffSchedule, getTimeSlotData } from '../../../Services/SpecialistMeetingServices';
import { AxiosResponse } from "axios";
import { utils } from "../../../helpers/utils";
import { CalendarEvent } from "../../../Types/CalendarEvent";
import { User } from "../../../Types/User";
import { userRole } from "../../../Constants/userRoles";


export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}
export interface MasterDataItem {
    value: string;
    id: number;
}

export interface MasterDataResponse {

}

export interface MasterData {
    assignedToOptions: PageSizeOption[],
}

export interface SMRData {
    employmentSpecialistMeetingRequestId: number;
    clientName: string;
    requestedBy: string;
    assignedTo: number;
    assignedToName: string;
    preferredDateTime: string;
    comments: string;
    requestedDate: string;
    helpTopics: string[];
    phomeNumber: string;
    email: string;
}

export interface TimeSlotDataPayload {
    staffId: number;
    selectedDate: string;
}

export interface TimeSlotData {
    meetingDate: string;
    startTime: string;
    endTime: string;
    requestedBy: string;
}

export interface MeetingEvent {
    meetingId: number;
    meetingDate: string;
    startTime: string;
    endTime: string;
    requesterName: string;
}

export interface MeetingTime {
    startTime: string;
    endTime: string;
}

export function RSAMeetingScheduler() {
    const history = useHistory();
    const currUser: User | undefined= utils.getUserFromLocalToken() as (User | undefined);
    const isReadOnly = (currUser?.Roles?.includes(userRole['Employment Coordinator']) === true || 
                        currUser?.Roles?.includes(userRole.Counselor) === true
                        ) ? false : true;
    let { MRId } = useParams<{ MRId: string }>();

    const [renderCounter, setRenderCounter] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [masterData, setMasterData] = useState<MasterData>();
    const [meetingRequestData, setMeetingRequestData] = useState<SMRData>();
    const [comments, setComments] = useState<string>('');
    const [selectedDay, setSelectedDay] = useState<Date | null>(null);
    const [selectedSpecialistId, setSelectedSpecialistId] = useState<number>(0);
    const [timeSlotData, setTimeSlotData] = useState<MeetingEvent[]>([]);
    const [meetingTime, setMeetingTime] = useState<MeetingTime | null>(null);
    const [errorMessage, setErrorMessage] = useState<string[]>([]);
    const [submittedSucess, setSubmittedSucess] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [monthEventData, setMonthEventData] = useState<Map<string, MeetingEvent[]>>(new Map<string, MeetingEvent[]>());
    const [isUserCoordinator, setIsUserCoordinator] = useState<boolean>((currUser?.Roles?.includes(userRole['Employment Coordinator']) === true) ? true : false);

    function handleDayClick(day: Date, data: DayModifiers) {
        if (day && data && !data.selected && !data.disabled && !data.outside) {
            setSelectedDay(day);
            // when day change, this day must already within current month's data
            let tempData = monthEventData.get(moment(day).format().split('T')[0]) || [];
            //console.log(tempData);
            setTimeSlotData(tempData);
            reRender();
            onDayChange();
        }
    }

    function reRender() {
        setRenderCounter((prevValue) => { return prevValue + 1 });
    }

    function handleSpecailistChange(newId: number) {
        setSelectedSpecialistId(newId);
        if (selectedDay) {
            _fetchTimeSlotData(selectedDay, newId, true,);
        } else {
            _fetchTimeSlotData(moment().toDate(), newId, true,);
        }
        setErrorMessage([]);
    }

    function onDayChange() {
        setMeetingTime(null);
        setErrorMessage([]);
    }

    function handleMonthChange(newMonth: Date) {
        _fetchTimeSlotData(newMonth, selectedSpecialistId, false);
    }

    function renderDay(date: Date, modifiers: DayModifiers) {
        const dateNumber = date.getDate();
        const isToday: boolean = moment().isSame(moment(date), 'day');
        const dataKey = moment(date).format().split('T')[0];
        let currDayList = monthEventData.get(dataKey) || [];
        let meetingCount = currDayList.length;
        let classNameBase = 'my-day-container ';
        if (meetingCount === 0) {
            classNameBase += 'empty';
        } else if (meetingCount >= 1) {
            classNameBase += 'partial-taken';
        }
        return (
            <div title={meetingCount == 0 ? 'No existing meeting' : `Existing meeting: ${meetingCount}`}
                className={classNameBase}
                style={{ minHeight: '3rem', position: 'relative', minWidth: '3rem', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'lighter', fontSize: 'small' }}>{dateNumber}</span>
                {
                    isToday &&
                    <span className="today-marker"></span>
                }
            </div>
        );
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        setIsLoading(true);
        getSpecialistMeetingListAssignedToMaster(false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, MasterDataResponse> = result as AxiosResponse;
            if (response && response.status === 200 && response.data) {
                let data = response.data as MasterDataItem[];
                let newMasterData: MasterData = {
                    assignedToOptions: []
                };

                let assignedToOptions: PageSizeOption[] = data.map((item, index) => {
                    return {
                        key: index,
                        value: item.id,
                        text: item.value
                    }
                });
                newMasterData.assignedToOptions = assignedToOptions;
                setMasterData(newMasterData);
            }
            callBack();
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchData: () => void = () => {

        //console.log(payload);
        setIsLoading(true);

        getSpecialistMeetingRequestById(MRId, false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, SMRData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {
                let data = response.data as SMRData;
                setMeetingRequestData(data);
                if(data && data.assignedTo && data.assignedTo > 0){
                    setTimeout(()=>{
                        handleSpecailistChange(data.assignedTo);
                    }, 200);
                }
                
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchTimeSlotData: (targetDate: Date, staffId: number, setSlotData?: boolean) => void = (targetDate, staffId, setSlotData = false) => {
        //console.log(payload);
        setIsLoading(true);

        let payload: TimeSlotDataPayload = {
            staffId: staffId,
            selectedDate: moment(targetDate).format(),
        };
        getTimeSlotData(payload, false).then((result) => {
            let response: AxiosResponse<unknown, TimeSlotData[]> = result as AxiosResponse;
            if (response.status === 200 && response.data) {
                let data = response.data as TimeSlotData[];
                let newData: MeetingEvent[] = data.map((item, index) => {
                    return {
                        meetingId: index + 1,
                        meetingDate: item.meetingDate,
                        startTime: item.startTime,
                        endTime: item.endTime,
                        requesterName: item.requestedBy,
                    };
                });
                //console.log(newData);
                monthEventData.clear();
                if (newData.length > 0) {
                    newData.forEach((meeting) => {
                        let currKey = meeting.meetingDate.split('T')[0];
                        if (monthEventData.get(currKey) == null) {
                            monthEventData.set(currKey, [meeting]);
                        } else {
                            monthEventData.get(currKey)?.push(meeting);
                        }
                    });
                };
                if (setSlotData === true) {
                    setTimeSlotData(monthEventData.get(moment(selectedDay || '').format().split('T')[0]) || []);
                    reRender();
                }
            }
            setIsLoading(false);
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }



    useEffect(() => {
        _fetchData();
        _fetchMasterData();
    }, []);

    function hasError() {
        let newError = [];
        if (!(selectedSpecialistId && selectedSpecialistId > 0)) {
            newError.push('Please select an Employment Coordinator');
        }
        if (!selectedDay) {
            newError.push('Please select a meeting date');
        }
        if ((selectedSpecialistId && selectedSpecialistId > 0) && meetingTime == null) {
            newError.push('Please select meeting start time and end time');
        }
        setErrorMessage(newError);
        return !(newError.length === 0);
    }

    function handleSaving() {
        let result = hasError();
        if (result === false) {
            let newPayload = {
                employmentSpecialistMeetingRequestId: MRId,
                assignedTo: selectedSpecialistId,
                meetingDate: moment(selectedDay || '').format(),
                startTime: meetingTime?.startTime,
                endTime: meetingTime?.endTime,
                comments: comments,
            };
            setIsSubmitting(true);
            saveRSAStaffSchedule(newPayload, false).then((result) => {
                setIsSubmitting(false);
                let response: AxiosResponse<unknown, unknown> = result as AxiosResponse;
                if (response.status === 200) {
                    setSubmittedSucess(true);
                }
            }, (error) => {
                setIsSubmitting(false);
                console.log(error);
            });
        }
    }

    const shouldShowTimeSlot: boolean = (selectedDay != null && selectedSpecialistId != 0 ? true : false);


    const handleDownloadCalendar=()=>{
        if(submittedSucess){
            let eventData: CalendarEvent = {
                startTime: moment((meetingTime?.startTime)).format(),
                endTime: moment(meetingTime?.endTime).format(),
                summary: 'Meeting for ' + (masterData?.assignedToOptions.filter((item) => { return item.value == selectedSpecialistId })[0].text),
                description: 'RSA Employment Coordinator Meeting for job seeker: ' + meetingRequestData?.requestedBy,
                location: 'Not specified.',
            };
            utils.downloadICSFile(eventData);
        }
    }
    
    return <Container className='providers-container'>
        <Header as='h1' style={{ marginTop: '2.0rem', marginBottom: '0.5rem' }}>RSA Employment Coordinator Meeting Scheduler</Header>
        <hr style={{ marginBottom: '1.0rem' }} />
        <Breadcrumb style={{ marginBottom: '1.0rem' }}>
            <Breadcrumb.Section><Link to='/dashboard'>Home</Link></Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section><Link to={'/RSASpecialistMeetingRequestList'}>RSA Employment Coordinator Meeting Requests</Link></Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>RSA Employment Coordinator Meeting Scheduler</Breadcrumb.Section>
        </Breadcrumb>

        <Grid>
            <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={4}>
                    <Segment color='red' style={{ minHeight: '34.2rem' }} loading={!meetingRequestData ? true : false}>
                        <div className='mb-2'>
                            <div>
                                <b>Client Name</b>
                            </div>
                            <div>
                                <span>{meetingRequestData?.clientName}</span>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Requested By</b>
                            </div>
                            <div>
                                <span>{meetingRequestData?.requestedBy}</span>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Help Topic</b>
                            </div>
                            <div>
                                <span>{meetingRequestData?.helpTopics.join(', ')}</span>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Preferred Date and Time</b>
                            </div>
                            <div>
                                <div dangerouslySetInnerHTML={{ __html: (meetingRequestData?.preferredDateTime || '').replaceAll('\n', "<br />\r\n") }}></div>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Comments</b>
                            </div>
                            <div>
                                <div dangerouslySetInnerHTML={{ __html: (meetingRequestData?.comments || '').replaceAll('\n', "<br />\r\n") }}></div>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Requested date</b>
                            </div>
                            <div>
                                <span>{moment(meetingRequestData?.requestedDate).format('MMMM D, YYYY')}</span>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Phone</b>
                            </div>
                            <div>
                                <span>{utils.formatPhoneNumber(meetingRequestData?.phomeNumber)}</span>
                            </div>
                        </div>
                        <div className='mb-2'>
                            <div>
                                <b>Email</b>
                            </div>
                            <div>
                                <span>{meetingRequestData?.email}</span>
                            </div>
                        </div>
                    </Segment>
                </Grid.Column>
                {
                    !submittedSucess &&
                    <>
                        <Grid.Column mobile={16} tablet={8} computer={7}>
                            <Form.Field>
                                <label><b>Employment Coordinator</b></label>
                                {
                                    !isUserCoordinator && 
                                    <Dropdown
                                    disabled={isSubmitting}
                                    style={{ marginTop: '0.5rem' }}
                                    fluid
                                    selection
                                    selectOnBlur={false}
                                    value={selectedSpecialistId}
                                    onChange={(event, { value }) => {
                                        handleSpecailistChange(value as number);
                                    }}
                                    placeholder='Select an specialist'
                                    options={masterData ? masterData.assignedToOptions : []}
                                />
                                }
                                {
                                    isUserCoordinator && 
                                    <>
                                        <div>{meetingRequestData?.assignedToName || ""}</div>
                                    </>
                                }
                            </Form.Field>
                            {
                                selectedSpecialistId > 0 &&
                                <>
                                    <p style={{ marginTop: '1.5rem', marginBottom:'0' }}>
                                        <b>Select a meeting date<br /></b>
                                    </p>
                                    <Segment basic style={{ margin: 0, padding: 0 }} loading={isLoading}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                            <DayPicker
                                                onDayClick={handleDayClick}
                                                selectedDays={selectedDay || []}
                                                disabledDays={[{ daysOfWeek: [0, 6] }, {
                                                    before: moment().add(1, 'day').toDate(),
                                                },]}
                                                renderDay={renderDay}
                                                fromMonth={new Date()}
                                                onMonthChange={(newMonth) => {
                                                    handleMonthChange(newMonth);
                                                }}
                                            />
                                            {
                                                selectedDay != null &&
                                                <b>You have selected: {moment(selectedDay).format('dddd, MMMM DD, YYYY')}</b>
                                            }
                                            <Message info>
                                                <div>
                                                    <div className="color-label empty"></div>
                                                    <span>Available</span>
                                                    <div className="color-label partial-taken"></div>
                                                    <span>Partially taken</span>
                                                </div>
                                            </Message>
                                        </div>
                                    </Segment>
                                </>
                            }
                            {
                                !(selectedSpecialistId > 0) &&
                                <Message info>
                                    <p>Please select a specialist first.</p>
                                </Message>
                            }
                        </Grid.Column>
                        {
                            shouldShowTimeSlot &&
                            <Grid.Column mobile={16} tablet={8} computer={5}>
                                <Segment basic style={{ margin: 0, padding: 0 }} loading={isLoading}>
                                    <TimeSlot
                                        isReadOnly = {isReadOnly}
                                        key={renderCounter}
                                        selectedDate={moment(selectedDay).format('MM/DD/YYYY ')}
                                        meetingsList={timeSlotData}
                                        onTimeChange={(newMeetingTime: MeetingTime) => {
                                            setMeetingTime(newMeetingTime);
                                            setErrorMessage([]);
                                        }}
                                    />
                                </Segment>
                            </Grid.Column>
                        }
                        {
                            !shouldShowTimeSlot &&
                            <Grid.Column mobile={16} tablet={8} computer={5}>
                                <Segment basic style={{ margin: 0, padding: 0 }} loading={isLoading}>
                                    <Message info>
                                        <p>Please select a meeting date to show the time slot.</p>
                                    </Message>
                                </Segment>
                            </Grid.Column>
                        }

                    </>
                }
                {
                    submittedSucess &&
                    <>
                        <Grid.Column mobile={16} tablet={8} computer={12}>
                            <Message success icon>
                                <Icon name='check circle outline' />
                                <Message.Header>

                                </Message.Header>
                                <Message.Content>
                                    <div>
                                        The meeting has been scheduled for {moment(selectedDay).format('dddd, MMMM D, YYYY')},
                                        {moment((meetingTime?.startTime)).format(' hh:mm A')} to {moment(meetingTime?.endTime).format('hh:mm A')}
                                        , with Employment Coordinator {masterData?.assignedToOptions.filter((item) => { return item.value == selectedSpecialistId })[0].text}.
                                    </div>
                                    <div style={{ marginTop: '0.5rem' }}>
                                        {/* <Button icon labelPosition='left' size='small'>
                                            <Icon name='mail outline' />
                                            <span>Send an email to the job seeker</span>
                                        </Button> */}
                                        <Button icon labelPosition='left' size='small' style={{marginLeft: '0.5rem'}} onClick={()=>{handleDownloadCalendar()}}>
                                            <Icon name='download' />
                                            <span>Download Calendar File <Icon name='calendar alternate' /></span>
                                        </Button>
                                    </div>
                                </Message.Content>
                            </Message>
                        </Grid.Column>
                    </>
                }
            </Grid.Row>
            {
                shouldShowTimeSlot && meetingTime != null && !submittedSucess &&
                <Grid.Row>
                    <Grid.Column mobile={16} tablet={16} computer={16}>
                        <Message info>
                            <Message.Header>
                                The meeting will be scheduled for {moment(selectedDay).format('dddd, MMMM D, YYYY')},
                                {moment(meetingTime.startTime).format(' hh:mm A')} to {moment(meetingTime.endTime).format('hh:mm A')}
                                , with Employment Coordinator {masterData?.assignedToOptions.filter((item) => { return item.value == selectedSpecialistId })[0].text}.
                            </Message.Header>
                        </Message>
                    </Grid.Column>
                </Grid.Row>
            }
            {
                errorMessage && errorMessage.length > 0 &&
                <Grid.Row>
                    <Grid.Column mobile={16} tablet={16} computer={16}>
                        <Message error
                            header='Please fix the following error(s) to proceed'
                            list={errorMessage}
                        >
                        </Message>
                    </Grid.Column>
                </Grid.Row>
            }
            {
                !submittedSucess && <Grid.Row>
                    <Grid.Column mobile={16} tablet={16} computer={16}>
                        <Form>
                            {
                                !isReadOnly &&
                                (<Form.TextArea
                                    label='Comments'
                                    value={comments}
                                    onChange={(event, { value }) => {
                                        setComments(value as string);
                                    }}
                                    disabled={isSubmitting}
                                />)
                            }
                            {
                                !isReadOnly &&
                                <Button type='button' loading={isSubmitting} disabled={isSubmitting} primary onClick={handleSaving}>Save</Button>
                            }
                            <Button type='button' disabled={isSubmitting} onClick={() => { history.goBack() }}>Cancel</Button>
                        </Form>
                    </Grid.Column>
                </Grid.Row>
            }

        </Grid>
    </Container>
}